import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Feedback as FeedbackIcon } from '@mui/icons-material';
import FeedbackForm from './FeedbackForm';

// `anchored` pins the button to a fixed spot off the bottom-right corner and
// disables dragging, for pages that need it to stay put.
const FloatingFeedbackButton = ({ anchored = false, anchorRight = 24, anchorBottom = 24 }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [formOpen, setFormOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const buttonRef = useRef(null);
  const startPositionRef = useRef(null);
  const currentPositionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (anchored) return;

    // Load saved position from localStorage
    const savedPosition = localStorage.getItem('feedbackButtonPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
        currentPositionRef.current = parsed;
      } catch (error) {
        console.error('Error loading saved position:', error);
        // Default position: bottom right
        const defaultPosition = {
          x: window.innerWidth - 80,
          y: window.innerHeight - 80
        };
        setPosition(defaultPosition);
        currentPositionRef.current = defaultPosition;
      }
    } else {
      // Default position: bottom right
      const defaultPosition = {
        x: window.innerWidth - 80,
        y: window.innerHeight - 80
      };
      setPosition(defaultPosition);
      currentPositionRef.current = defaultPosition;
    }
  }, [anchored]);

  const handleMouseDown = (e) => {
    if (anchored) return;
    if (e.button !== 0) return; // Only handle left mouse button
    
    setIsDragging(true);
    setHasMoved(false);
    const rect = buttonRef.current.getBoundingClientRect();
    startPositionRef.current = { x: e.clientX, y: e.clientY };
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      // Check if mouse has moved significantly (more than 5px)
      if (startPositionRef.current) {
        const deltaX = Math.abs(e.clientX - startPositionRef.current.x);
        const deltaY = Math.abs(e.clientY - startPositionRef.current.y);
        if (deltaX > 5 || deltaY > 5) {
          setHasMoved(true);
        }
      }

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;
      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      const newPosition = { x: constrainedX, y: constrainedY };
      currentPositionRef.current = newPosition;
      setPosition(newPosition);
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setHasMoved(false);
        startPositionRef.current = null;
        // Save position to localStorage using ref to get latest value
        localStorage.setItem('feedbackButtonPosition', JSON.stringify(currentPositionRef.current));
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleClick = (e) => {
    // Only open form if we didn't drag (moved less than 5px)
    if (!hasMoved) {
      setFormOpen(true);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
  };

  return (
    <>
      <div
        ref={buttonRef}
        style={
          anchored
            ? {
                position: 'fixed',
                right: `${anchorRight}px`,
                bottom: `${anchorBottom}px`,
                zIndex: 9999
              }
            : {
                position: 'fixed',
                left: `${position.x}px`,
                top: `${position.y}px`,
                zIndex: 9999,
                cursor: isDragging ? 'grabbing' : 'grab'
              }
        }
        onMouseDown={handleMouseDown}
      >
        <Tooltip title="Report Feedback" placement="left">
          <IconButton
            color="primary"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              width: 56,
              height: 56,
              boxShadow: 3,
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 6
              },
              userSelect: 'none'
            }}
            onClick={handleClick}
            onMouseDown={(e) => {
              // Prevent text selection while dragging
              e.preventDefault();
            }}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      </div>
      <FeedbackForm
        open={formOpen}
        onClose={handleFormClose}
      />
    </>
  );
};

export default FloatingFeedbackButton;

